import { isDefined } from 'common/utils';

import { HierarchyOptionT } from './HierarchyOption';
import { Option } from './HierarchyOptions';

const normalizeString = (str: string) => str.trim().toLowerCase();

export const isSearchingStringFound = (search: string, values: (string | undefined)[]) => {
  const normalizedValues = values.filter(isDefined).map(normalizeString);
  return normalizedValues.some(label => label.includes(search) || search.includes(label));
};

export const buildHierarchyOptions = (
  options: Option[],
  topLevelOptions: Option[] = options.filter(option => option.parentId === null),
): HierarchyOptionT[] =>
  topLevelOptions.map(topLevelOption => ({
    label: topLevelOption.label,
    value: topLevelOption.value,
    secondaryContent: topLevelOption.secondaryContent,
    children: buildHierarchyOptions(
      options,
      options.filter(option => topLevelOption.childrenIds.includes(option.value)),
    ),
  }));

export const allDescendantsAreSelected = (hierarchyOption: HierarchyOptionT, hierarchyValues: string[]): boolean => {
  if (hierarchyValues.length === 0) {
    return false;
  }

  if (hierarchyValues.includes(hierarchyOption.value)) {
    return true;
  }

  if (hierarchyOption.children.length === 0) {
    return hierarchyValues.includes(hierarchyOption.value);
  }

  return !hierarchyOption.children.some(
    child => !allDescendantsAreSelected(child, hierarchyValues) && !hierarchyValues.includes(child.value),
  );
};

export const searchDescendantsHierarchy = (hierarchyOption: HierarchyOptionT, search: string): boolean =>
  isSearchingStringFound(search, [hierarchyOption.label]) ||
  hierarchyOption.children.some(child => searchDescendantsHierarchy(child, search));

export const searchHigherHierarchy = (
  optionId: string,
  search: string,
  optionsByIdMap: Map<string, Option>,
): boolean => {
  const option = optionsByIdMap.get(optionId);
  if (option === undefined) {
    return false;
  }

  if (isSearchingStringFound(search, [option.label])) {
    return true;
  }

  const { parentId } = option;
  if (parentId === null) {
    return false;
  }

  return searchHigherHierarchy(parentId, search, optionsByIdMap);
};

export const searchHierarchy = (
  hierarchyOption: HierarchyOptionT,
  search: string,
  optionsByIdMap: Map<string, Option>,
): boolean => {
  const searchTerms = normalizeString(search).split(/[\s]+/);

  return searchTerms.every(
    term =>
      searchDescendantsHierarchy(hierarchyOption, term) ||
      searchHigherHierarchy(hierarchyOption.value, term, optionsByIdMap),
  );
};

export const isDescendantOfNode = (childNode: string, node: string, optionsByIdMap: Map<string, Option>): Boolean => {
  const option = optionsByIdMap.get(childNode);

  if (!option) {
    return false;
  }

  if (childNode === node) {
    return false;
  }

  if (option.parentId === null) {
    return false;
  }

  if (option.parentId === node) {
    return true;
  }

  return isDescendantOfNode(option.parentId, node, optionsByIdMap);
};

export const groupValuesByHierarchy = (hierarchyValues: string[], optionsByIdMap: Map<string, Option>): string[] => {
  const newHierarchyValues = hierarchyValues.reduce<string[]>(function reducer(values, value): string[] {
    const option = optionsByIdMap.get(value);
    if (!option) {
      return values;
    }

    if (values.some(v => isDescendantOfNode(option.value, v, optionsByIdMap))) {
      return values.filter(v => v !== option.value);
    }

    if (option.parentId === null) {
      return values;
    }

    const parentOption = optionsByIdMap.get(option.parentId);
    if (!parentOption) {
      return values;
    }

    const areChildrenSelected = !parentOption.childrenIds.some(child => !values.includes(child));
    const filteredValue = values.filter(v => !parentOption.childrenIds.includes(v));

    if (values.includes(parentOption.value)) {
      return filteredValue;
    }

    if (areChildrenSelected) {
      return [...filteredValue, parentOption.value];
    }

    return reducer(values, option.parentId);
  }, hierarchyValues);

  if (newHierarchyValues.length === hierarchyValues.length) {
    return newHierarchyValues;
  }

  return groupValuesByHierarchy(newHierarchyValues, optionsByIdMap);
};

export const removeValueFromHierarchy = (
  removingValue: string,
  values: string[],
  optionsByIdMap: Map<string, Option>,
): string[] => {
  const newValues = values.reduce<string[]>(function reducer(result, value): string[] {
    if (isDescendantOfNode(removingValue, value, optionsByIdMap)) {
      const option = optionsByIdMap.get(value);
      if (!option) {
        return result;
      }

      const childValues = option.childrenIds.reduce(reducer, []).filter(v => v !== removingValue);

      return [...result, ...childValues];
    }

    return [...result, value];
  }, []);

  if (newValues.length === values.length) {
    return newValues;
  }

  return removeValueFromHierarchy(removingValue, newValues, optionsByIdMap);
};
